import { useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../contexts/AuthContext'

import { Wrapper } from 'components'

import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  Input,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from 'keep-react'
import { BrokerDetails, BrokerUser, BrokerUsers } from 'api/types/broker'
import { brokerService } from 'api/services/broker'
import { useNavigate } from 'react-router-dom'
import { RouteNamesAuth } from 'routes/enum'
import { authService } from 'api/services/auth'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { InviteUserItem } from 'api/types/auth'
import { format } from 'date-fns'
import { Trash } from 'phosphor-react'
import { isoCountries } from 'utils/countries'

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Email inválido').required('O email é obrigatório'),
})

export const ProfileBroker = () => {
  const { accessLevel, currentBroker } = useContext(AuthContext)
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [broker, setBroker] = useState<BrokerDetails | null>(null)
  const [brokerUsers, setBrokerUsers] = useState<BrokerUser[]>([])
  const [brokerInvites, setBrokerInvites] = useState<InviteUserItem[]>([])
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false)
  const [isSendingInvite, setIsSendingInvite] = useState<boolean>(false)

  const brokerId = currentBroker?.BrokerNo || 0

  const getBrokerDetails = async () => {
    setIsLoading(true)
    if (!brokerId) return
    try {
      const brokerResponse = (await brokerService.getBroker({
        brokerNo: brokerId,
      })) as BrokerDetails
      setBroker(brokerResponse)

      try {
        const usersResponse = (await brokerService.getBrokerContacts({
          brokerNo: brokerId,
        })) as BrokerUsers
        setBrokerUsers(usersResponse.Contact)

        try {
          const invitesResponse = (await authService.getUserInviteList({
            brokerNo: brokerId,
          })) as InviteUserItem[]
          setBrokerInvites(invitesResponse)
        } catch (error) {
          console.error('Error fetching broker invites:', error)
        }
      } catch (error) {
        console.error('Error fetching broker users:', error)
        toast.error('Erro ao obter utilizadores do broker.')
      }
    } catch (error) {
      console.error('Error fetching broker details:', error)
      toast.error('Erro ao obter detalhes do broker.')
    } finally {
      setIsLoading(false)
    }
  }

  const inviteFormik = useFormik({
    initialValues: { email: '' },
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema,
    onSubmit: async values => {
      setIsSendingInvite(true)
      try {
        await authService.generateInvite({
          brokerNo: brokerId,
          email: values.email,
        })
        toast.success('Convite enviado com sucesso.')
        inviteFormik.resetForm()
      } catch (error: any) {
        toast.error(error.response.data.detail)
      } finally {
        setIsSendingInvite(false)
        setShowInviteModal(false)
        getBrokerDetails()
      }
    },
  })

  const handleDeleteInvite = async (email: string) => {
    try {
      await authService.deleteInvite({
        email: email,
        brokerNo: brokerId,
      })
      setBrokerInvites(prevInvites =>
        prevInvites.filter(invite => invite.email !== email),
      )
      toast.success('Convite eliminado com sucesso.')
    } catch (error) {
      toast.error('Erro ao eliminar convite.')
    }
  }

  const handleAccessLevelChange = async (
    contactNo: number,
    newAccessLevel: number,
  ) => {
    try {
      await brokerService.changeBrokerContactAccessLevel({
        brokerNo: brokerId,
        contactNo,
        accessLevel: newAccessLevel,
      })
      setBrokerUsers(prevUsers =>
        prevUsers.map(user =>
          user.ContactNo === contactNo
            ? {
                ...user,
                Access: {
                  ...user.Access,
                  AccessLevel: newAccessLevel.toString(),
                },
              }
            : user,
        ),
      )
      toast.success('Nível de acesso atualizado com sucesso.')
    } catch (error) {
      toast.error('Erro ao atualizar nível de acesso.')
    }
  }

  useEffect(() => {
    getBrokerDetails()
  }, [brokerId])

  useEffect(() => {
    if (currentBroker?.IsGroup) {
      navigate(RouteNamesAuth.DASHBOARD, { replace: true })
    }
  }, [currentBroker])

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        <h2 className="uppercase text-sm font-medium text-metal-900">
          Gestão de broker
        </h2>

        <h1
          className={`pb-10 text-xxxl font-medium ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-600'}`}
        >
          {broker ? broker.Broker.Name : ' '}
        </h1>

        {isLoading ? (
          <Card className="p-4 w-full max-w-full">
            <Spinner color="primary" className="mx-auto" />
          </Card>
        ) : !broker ? (
          <Card className="p-4 w-full max-w-full">
            <div className="text-heading-6 text-metal-400">
              Apólice indisponível
            </div>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card className="p-4 w-full max-w-full">
              <div className="grid grid-cols-2 gap-4">
                <h3 className="text-body-4 text-metal-900">
                  NIF: {broker.Broker.ID}
                </h3>
                <div>
                  <h3 className="text-body-4 text-metal-900">Endereço:</h3>
                  <p className="text-body-4 text-metal-600">
                    {broker.Broker.CorrespondanceAddress.AddressLine1}
                    <br />
                    {broker.Broker.CorrespondanceAddress.AddressLine2}
                    <br />
                    {broker.Broker.CorrespondanceAddress.AddressLine4}
                    <br />
                    {broker.Broker.CorrespondanceAddress.Postcode} <br />
                    {isoCountries.find(
                      country =>
                        country.code ===
                        broker.Broker.CorrespondanceAddress.Country.toLocaleUpperCase(),
                    )?.country || broker.Broker.CorrespondanceAddress.Country}
                  </p>
                </div>
              </div>
            </Card>

            <div className="mb-4">
              <div className="text-body-4 mb-4 bg-metal-900 rounded-lg text-white p-4 space-y-2">
                <h3
                  className={`text-body-4 ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-400'} font-bold uppercase`}
                >
                  Legenda
                </h3>
                <p className="text-body-5 ">
                  <span
                    className={`font-semibold ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-400'}`}
                  >
                    Read-Only:
                  </span>{' '}
                  Este perfil de utilizador tem permissões exclusivamente de
                  consulta. Pode efectuar simulações e aceder a informação de
                  carácter geral (por exemplo, campanhas). O acesso à listagem
                  de apólices e recibos encontra-se totalmente restrito.
                </p>
                <p className="text-body-5 ">
                  <span
                    className={`font-semibold ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-400'}`}
                  >
                    Junior:
                  </span>{' '}
                  Este perfil dispõe das mesmas permissões do utilizador
                  Read-Only, com a adição da possibilidade de consultar as
                  apólices por si próprio criadas, bem como os respectivos
                  recibos. O acesso a apólices e recibos criados por outros
                  utilizadores não é permitido.
                </p>
                <p className="text-body-5 ">
                  <span
                    className={`font-semibold ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-400'}`}
                  >
                    Senior:
                  </span>{' '}
                  Este perfil inclui todas as permissões atribuídas ao
                  utilizador Junior, permitindo adicionalmente a consulta da
                  totalidade das apólices e recibos associados ao Mediador,
                  independentemente do utilizador que os tenha criado.
                </p>
                <p className="text-body-5 ">
                  <span
                    className={`font-semibold ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-400'}`}
                  >
                    Admin:
                  </span>{' '}
                  Este perfil possui todas as permissões do utilizador Senior,
                  acrescidas de capacidades administrativas, nomeadamente a
                  gestão de acessos e permissões dos utilizadores da plataforma.
                </p>
              </div>
            </div>

            <Card className="p-4 w-full max-w-full">
              <div className="flex gap-4 items-start justify-between mb-4">
                <h3
                  className={`text-body-2 ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-600'} pb-6`}
                >
                  Utilizadores
                </h3>
                <Button
                  disabled={accessLevel! < 3}
                  color="primary"
                  variant="softBg"
                  onClick={() => setShowInviteModal(true)}
                  className={`${currentBroker?.IsPrimePartner ? 'bg-prime hover:bg-black text-white hover:text-white' : 'bg-primary-600'}`}
                >
                  Convidar Utilizador
                </Button>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Cargo</TableHead>
                    <TableHead></TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                {brokerUsers.length > 0 && (
                  <TableBody>
                    {brokerUsers.map((user, index) => (
                      <TableRow key={`user__${index}`}>
                        <TableCell>{user.Name}</TableCell>
                        <TableCell className="normal-case">
                          {user.Email}
                        </TableCell>

                        <TableCell>
                          {broker.Broker.Active ? 'Ativo' : 'Inativo'}
                        </TableCell>
                        <TableCell>
                          <Badge
                            color="secondary"
                            variant="base"
                            className="whitespace-nowrap"
                          >
                            {user.Access.AccessLevel === '4'
                              ? 'Broker Master'
                              : user.Access.AccessLevel === '3'
                                ? 'Admin'
                                : user.Access.AccessLevel === '2'
                                  ? 'Senior'
                                  : user.Access.AccessLevel === '1'
                                    ? 'Junior'
                                    : 'Read Only'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {accessLevel &&
                            accessLevel >= 3 &&
                            Number(user.Access.AccessLevel) < 3 && (
                              <ButtonGroup>
                                <Button
                                  color="secondary"
                                  variant="softBg"
                                  size="sm"
                                  position="start"
                                  disabled={user.Access.AccessLevel === '0'}
                                  onClick={() =>
                                    handleAccessLevelChange(user.ContactNo, 0)
                                  }
                                >
                                  Read Only
                                </Button>
                                <Button
                                  color="secondary"
                                  variant="softBg"
                                  size="sm"
                                  position="center"
                                  disabled={user.Access.AccessLevel === '1'}
                                  onClick={() =>
                                    handleAccessLevelChange(user.ContactNo, 1)
                                  }
                                >
                                  Junior
                                </Button>
                                {accessLevel === 4 && (
                                  <>
                                    <Button
                                      color="secondary"
                                      variant="softBg"
                                      size="sm"
                                      position="center"
                                      disabled={user.Access.AccessLevel === '2'}
                                      onClick={() =>
                                        handleAccessLevelChange(
                                          user.ContactNo,
                                          2,
                                        )
                                      }
                                    >
                                      Senior
                                    </Button>
                                    <Button
                                      color="secondary"
                                      variant="softBg"
                                      size="sm"
                                      position="end"
                                      disabled={user.Access.AccessLevel === '3'}
                                      onClick={() =>
                                        handleAccessLevelChange(
                                          user.ContactNo,
                                          3,
                                        )
                                      }
                                    >
                                      Admin
                                    </Button>
                                  </>
                                )}
                                {accessLevel === 3 && (
                                  <>
                                    <Button
                                      color="secondary"
                                      variant="softBg"
                                      size="sm"
                                      position="end"
                                      disabled={user.Access.AccessLevel === '2'}
                                    >
                                      Senior
                                    </Button>
                                    <Button
                                      color="secondary"
                                      variant="softBg"
                                      size="sm"
                                      position="end"
                                      disabled={user.Access.AccessLevel === '3'}
                                      onClick={() =>
                                        handleAccessLevelChange(
                                          user.ContactNo,
                                          3,
                                        )
                                      }
                                    >
                                      Admin
                                    </Button>
                                  </>
                                )}
                              </ButtonGroup>
                            )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                )}
              </Table>
            </Card>

            <Card className="p-4 w-full max-w-full">
              <h3
                className={`text-body-2 ${currentBroker?.IsPrimePartner ? 'text-prime' : 'text-primary-600'} pb-6`}
              >
                Convites
              </h3>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Data Expiração</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {brokerInvites.map((invite, index) => (
                    <TableRow key={`invite__${index}`}>
                      <TableCell className="normal-case">
                        {invite.email}
                      </TableCell>
                      <TableCell>
                        {format(new Date(invite.expirationDate), 'dd/MM/yyyy')}
                      </TableCell>
                      <TableCell>
                        <Button
                          color="primary"
                          shape="icon"
                          variant="outline"
                          onClick={() => {
                            handleDeleteInvite(invite.email)
                          }}
                        >
                          <Trash size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </div>
        )}
      </Wrapper>

      <Modal
        open={showInviteModal}
        onOpenChange={() => setShowInviteModal(false)}
      >
        <ModalContent>
          <ModalHeader>
            <ModalTitle className="pb-4">Convidar Utilizador</ModalTitle>
            <ModalDescription className="sr-only hidden">
              Modal para convidar um novo utilizador para o broker.
            </ModalDescription>
            <form onSubmit={inviteFormik.handleSubmit}>
              <div>
                <Input
                  name="email"
                  placeholder="Email"
                  type="email"
                  onChange={inviteFormik.handleChange}
                  className={`${
                    inviteFormik.errors.email ? 'border-error-500' : ''
                  }`}
                />
                {inviteFormik.errors.email && (
                  <p className="text-error-500 text-body-5 mt-1">
                    {inviteFormik.errors.email}
                  </p>
                )}
              </div>
            </form>
          </ModalHeader>
          <ModalFooter>
            <nav className="flex items-center justify-between gap-4 w-full mt-4">
              <Button
                color="secondary"
                variant="outline"
                onClick={() => setShowInviteModal(false)}
                className="w-full"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                color="primary"
                className="w-full"
                disabled={isSendingInvite}
                onClick={() => inviteFormik.handleSubmit()}
              >
                {isSendingInvite ? 'Enviando...' : 'Enviar Convite'}
              </Button>
            </nav>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
